import { debugLog } from "../utils/debugLog.js";
import React, { Fragment, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Airtable from 'airtable';
import axios from 'axios';
import AsyncSelect from "react-select/async";
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { AppContext } from '../AppContext.js';
import Notification from '../Notification.js';
import { useUser } from "@clerk/clerk-react";
import { extractDomain, setShowNotif, setContents } from '../helpers.js';
import { addGrantIdToMissingInfoFor } from '../utils/missingInfoFor.js';
import { industryOptions, countryOptions, regionCodes } from '../helperData.js';
import { PRODUCTION_REQUIRED_FIELDS } from '../utils/deepResearchGrants.js';

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const base = new Airtable({ apiKey }).base(baseId);

export { PRODUCTION_REQUIRED_FIELDS };

const AddGrantModal = ({ isOpen, onClose, mergeMode = false, stagedGrant = null, onMergeComplete }) => {
	const { grants, setGrants } = useContext(AppContext);
	const { user } = useUser();
	
	// State for URL scraping
	const [url, setUrl] = useState('');
	const [previewData, setPreviewData] = useState(null);
	const [potentialDuplicates, setPotentialDuplicates] = useState([]);
	const [showPreview, setShowPreview] = useState(false);
	const [hasScraped, setHasScraped] = useState(false);
	const [isScraping, setIsScraping] = useState(false);
	const [showDebug, setShowDebug] = useState(false);
	const [showDebugJson, setShowDebugJson] = useState(false);
	const [leftStagedTab, setLeftStagedTab] = useState('details'); // 'details' | 'website' | 'inputMatcher' (merge mode left column)
	const [isDuplicate, setIsDuplicate] = useState(false);
	const [scrapedRecordId, setScrapedRecordId] = useState(null); // Track the record ID from scraping
	
	// Company selector state
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [isLoadingUserCompany, setIsLoadingUserCompany] = useState(false);
	const [defaultCompanyOption, setDefaultCompanyOption] = useState(null);
	
	// State for form
	const [formData, setFormData] = useState({
		Name: '',
		Amount: '',
		Deadline: '',
		GrantUrl: '',
		Description: '',
		Eligibility_Criteria: '',
		Eligible_Industry: '',
		Eligible_Region: '',
		Eligible_Employees: '',
		Eligible_Founded: '',
		Eligible_Revenue: '',
		ExcitingBenefitContent: ''
	});

	// Region options: countries (from countryOptions) + all provinces/states from regionCodes (title-case labels)
	const regionOptions = useMemo(() => {
		const countryList = countryOptions.map(c => ({ value: c.label, label: c.label }));
		const subRegions = [];
		Object.values(regionCodes || {}).forEach((countryRegions) => {
			Object.values(countryRegions || {}).forEach((name) => {
				const label = String(name).replace(/\b\w/g, (c) => c.toUpperCase());
				subRegions.push({ value: label, label });
			});
		});
		return [...countryList, ...subRegions];
	}, []);

	const eligibleIndustryOptions = useMemo(() => {
		const priority = [
			{ value: 'All', label: 'All' },
			{ value: 'Technology', label: 'Technology' },
		];
		const seen = new Set(priority.map((o) => o.value.toLowerCase()));
		const rest = (industryOptions || []).filter((o) => {
			const key = String(o.value || o.label || '').toLowerCase();
			if (!key || seen.has(key)) return false;
			seen.add(key);
			return true;
		});
		return [...priority, ...rest];
	}, []);

	// Parse stored multi value (string or array) into react-select value array
	const parseMultiSelect = (stored, options) => {
		const arr = Array.isArray(stored) ? stored : (stored || '').split(',').map((s) => String(s).trim()).filter(Boolean);
		return arr.map((v) => options.find((o) => o.value === v || o.label === v) || { value: v, label: v });
	};
	const multiToStored = (selected) => (selected && selected.length ? selected.map((o) => o.value).join(', ') : '');

	const deadlinePresetOptions = [
		{ value: 'Ongoing', label: 'Ongoing' },
		{ value: 'Closed', label: 'Closed' },
		{ value: 'Temporarily Closed', label: 'Temporarily Closed' },
	];
	const isDeadlineDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');
	
	// URL check (broken link validator)
	const [urlCheckState, setUrlCheckState] = useState({ status: 'idle', message: null, statusCode: null }); // status: 'idle' | 'checking' | 'ok' | 'broken' | 'error'

	// Notification states
	const [submitGrantNotif, setSubmitGrantNotif] = useState(false);
	const [errorGrantNotif, setErrorGrantNotif] = useState({ show: false, contents: null });
	
	// EventSource reference (if needed from parent)
	let eventSource = null;
	const setEventSource = (source) => {
		eventSource = source;
	};
	
	// Normalize URL for duplicate checking
	const normalizeUrl = (url) => {
		try {
			const u = new URL(url);
			// Remove trailing slash, lowercase, and ignore protocol
			return u.hostname.toLowerCase() + u.pathname.replace(/\/$/, '').toLowerCase();
		} catch {
			return url.trim().toLowerCase().replace(/\/$/, '');
		}
	};
	
	
	// Helper function to populate form data from preview
	const populateFormFromPreview = (data, scrapedUrl = '') => {
		if (data && data.fields) {
			// Handle Airtable record format
			const toStr = (v) => (Array.isArray(v) ? v.join(', ') : (v != null && v !== '' ? String(v) : ''));
			setFormData({
				Name: data.fields.Name || '',
				Amount: data.fields.Amount || '',
				Deadline: data.fields.Deadline || '',
				GrantUrl: data.fields.GrantUrl || scrapedUrl,
				Description: data.fields.Description || '',
				Eligibility_Criteria: data.fields.Eligibility_Criteria || '',
				Eligible_Industry: toStr(data.fields.Eligible_Industry),
				Eligible_Region: toStr(data.fields.Eligible_Region),
				Eligible_Employees: data.fields.Eligible_Employees || '',
				Eligible_Founded: data.fields.Eligible_Founded || '',
				Eligible_Revenue: toStr(data.fields.Eligible_Revenue),
				ExcitingBenefitContent: toStr(data.fields.ExcitingBenefitContent)
			});
		} else if (data) {
			// Handle direct object format from scraper
			const toStr = (v) => (Array.isArray(v) ? v.join(', ') : (v != null && v !== '' ? String(v) : ''));
			setFormData(prev => ({
				...prev,
				Name: data.Name || prev.Name,
				Amount: data.Amount || prev.Amount,
				Deadline: data.Deadline || prev.Deadline,
				GrantUrl: data.GrantUrl || scrapedUrl,
				Description: data.Description || prev.Description,
				Eligibility_Criteria: data.Eligibility_Criteria || prev.Eligibility_Criteria,
				Eligible_Industry: toStr(data.Eligible_Industry) || prev.Eligible_Industry,
				Eligible_Region: toStr(data.Eligible_Region) || prev.Eligible_Region,
				Eligible_Employees: data.Eligible_Employees || prev.Eligible_Employees,
				Eligible_Founded: data.Eligible_Founded || prev.Eligible_Founded,
				Eligible_Revenue: toStr(data.Eligible_Revenue) || prev.Eligible_Revenue,
				ExcitingBenefitContent: toStr(data.ExcitingBenefitContent) || prev.ExcitingBenefitContent
			}));
		}
	};

	// Handle URL scraping
	const handleKeyDown = async (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			await handleScrape();
		}
	};

	const handleScrape = async () => {
		const link = url.trim();
		if (!link) return;
		
		setIsScraping(true);
		setHasScraped(false);
		setIsDuplicate(false);
		
		// Close EventSource temporarily to free up resources (if available)
		if (eventSource) {
			eventSource.close();
		}
		
		try {
			debugLog('Submitted URL:', link);
			const inputNorm = normalizeUrl(link);
			debugLog('Normalized input URL:', inputNorm);
			// Fetch all grants and compare normalized URLs
			const allGrants = await base("deduped_grants")
				.select({fields: ["GrantUrl", "Name", "Amount", "Deadline", "Description", "Eligibility_Criteria", "Eligible_Industry", "Eligible_Region", "Eligible_Employees", "Eligible_Founded", "Eligible_Revenue", "ExcitingBenefitContent"]})
				.all();
			debugLog('Fetched grants from Airtable:', allGrants.length);
			if (!allGrants || allGrants.length === 0) {
				debugLog('No grants returned from Airtable.');
				setErrorGrantNotif({
					show: true,
					contents: {
						type: "warning",
						heading: "No Grants Found",
						message: "No grants were returned from Airtable. Please try again later."
					}
				});
				setIsScraping(false);
				return;
			}
			let found = false;
			for (const g of allGrants) {
				if (g.fields.GrantUrl) {
					const norm = normalizeUrl(g.fields.GrantUrl);
					debugLog(`Comparing: [${inputNorm}] vs [${norm}] for grant:`, g.fields.Name);
					if (norm === inputNorm) {
						debugLog('Duplicate found:', g);
						setPreviewData(g);
						setShowPreview(true);
						setIsDuplicate(true);
						setHasScraped(true);
						setScrapedRecordId(g.id); // Track the existing record ID
						populateFormFromPreview(g, link);
						setErrorGrantNotif({
							show: true,
							contents: {
								type: "warning",
								heading: "Grant Already Exists",
								message: "This grant URL is already in the database. You can edit and update it, or add it to a company's earnings roadmap."
							}
						});
						found = true;
						break;
					}
				}
			}
			if (!found) {
				debugLog('No duplicate found for:', link);
				// If grant doesn't exist, proceed with scraping
				debugLog(`Submitting URL for scraping: ${link}`);
				
				// Use Next.js API route - relative URL works in production, localhost in dev
				const apiUrl = process.env.NODE_ENV === 'production' 
					? '/api/scraper'
					: 'http://localhost:3001/api/scraper';
				
				const response = await axios({
					method: 'post',
					url: apiUrl,
					data: { link },
					timeout: 120000, // 2 minutes timeout
					headers: {
						'Content-Type': 'application/json'
					}
				});
				debugLog('Scraper response:', response.data);
				// Set preview data
				setPreviewData(response.data);
				setShowPreview(true);
				setHasScraped(true);
				setIsDuplicate(false);
				// Track the record ID that was created by the scraper
				setScrapedRecordId(response.data.id);
				populateFormFromPreview(response.data, link);
				// Check for potential duplicates (from production table; exclude archived)
				const domain = new URL(link).hostname;
				const duplicates = (grants || []).filter(grant => {
					if (String(grant.fields?.Archived) === "true") return false;
					try {
						const grantDomain = new URL(grant.fields.GrantUrl).hostname;
						return grantDomain === domain;
					} catch (e) {
						return false;
					}
				});
				setPotentialDuplicates(duplicates);
			}
		} catch(error) {
			console.error("Airtable or Scraper API Error:", error);
			console.error("Error response data:", error.response?.data);
			
			let errorMessage = `An error occurred while checking for duplicates or scraping: ${error.message}`;
			let errorHeading = "Error Occurred";
			
			// Handle specific error types
			if (error.code === 'ECONNABORTED') {
				errorHeading = "Request Timeout";
				errorMessage = "The scraping process is taking longer than expected. This may happen with large pages. Please try again or check Airtable to see if the grant was created.";
			} else if (error.response?.status === 500) {
				errorHeading = "Server Error";
				const serverError = error.response?.data?.error || error.response?.data?.message;
				const errorDetails = error.response?.data?.details;
				errorMessage = serverError || "The server encountered an error while processing your request.";
				
				// Include details in development
				if (errorDetails && process.env.NODE_ENV === 'development') {
					errorMessage += `\n\nDetails: ${errorDetails}`;
				}
				
				// Log full error for debugging
				console.error("Full server error:", error.response?.data);
			} else if (error.response?.status === 400) {
				errorHeading = "Invalid Request";
				errorMessage = error.response?.data?.error || "The request was invalid. Please check the URL and try again.";
			} else if (!error.response) {
				errorHeading = "Connection Error";
				errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
			}
			
			setErrorGrantNotif({
				show: true,
				contents: {
					type: "error",
					heading: errorHeading,
					message: errorMessage
				}
			});
		} finally {
			setIsScraping(false);
		}
	};
	
	// Handle form input changes
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		if (name === 'GrantUrl') setUrlCheckState({ status: 'idle', message: null, statusCode: null });
		setFormData(prev => ({
			...prev,
			[name]: name === 'Amount' ? Number(value) : value
		}));
	};

	// Check if Grant URL is reachable (not broken)
	const handleCheckUrl = async () => {
		const url = (formData.GrantUrl || '').trim();
		if (!url) {
			setUrlCheckState({ status: 'error', message: 'Enter a URL first.', statusCode: null });
			return;
		}
		try {
			new URL(url);
		} catch {
			setUrlCheckState({ status: 'error', message: 'Invalid URL format.', statusCode: null });
			return;
		}
		setUrlCheckState({ status: 'checking', message: null, statusCode: null });
		try {
			const res = await fetch('/api/check-url', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ url }),
			});
			const data = await res.json().catch(() => ({}));
			if (data.ok) {
				setUrlCheckState({ status: 'ok', message: data.message || `URL is reachable (${data.status})`, statusCode: data.status });
			} else {
				setUrlCheckState({ status: 'broken', message: data.message || data.error || 'URL may be broken.', statusCode: data.status });
			}
		} catch (err) {
			setUrlCheckState({ status: 'error', message: err.message || 'Could not check URL. Try again.', statusCode: null });
		}
	};
	
	// Handle form submission
	const handleSubmitGrant = async () => {
		// Validate required fields
		const requiredFields = ['Name', 'Amount', 'Deadline', 'GrantUrl'];
		const missingFields = requiredFields.filter(field => !formData[field]);
		
		if (missingFields.length > 0) {
			setErrorGrantNotif({
				show: true,
				contents: {
					type: "error",
					heading: "Missing Required Fields",
					message: `Please fill in the following required fields: ${missingFields.join(', ')}`
				}
			});
			return;
		}

		// Check for duplicate grant name only if creating new record (not updating)
		if (!scrapedRecordId) {
			try {
				const existingGrants = await base("deduped_grants")
					.select({
						filterByFormula: `{Name} = '${formData.Name}'`,
						maxRecords: 1
					})
					.firstPage();

				if (existingGrants && existingGrants.length > 0) {
					setErrorGrantNotif({
						show: true,
						contents: {
							type: "error",
							heading: "Duplicate Grant Name",
							message: "A grant with this name already exists. Please choose a different name."
						}
					});
					return;
				}
			} catch (error) {
				console.error('Error checking for duplicate grant:', error);
				setErrorGrantNotif({
					show: true,
					contents: {
						type: "error",
						heading: "Error",
						message: "Failed to check for duplicate grant name. Please try again."
					}
				});
				return;
			}
		}

		// Validate Amount: at least 1000
		const amountNum = Number(formData.Amount);
		if (isNaN(amountNum) || amountNum < 1000) {
			setErrorGrantNotif({
				show: true,
				contents: {
					type: "error",
					heading: "Invalid Amount",
					message: "Grant amount must be at least 1,000."
				}
			});
			return;
		}

		// Validate URL format
		try {
			new URL(formData.GrantUrl);
		} catch (e) {
			setErrorGrantNotif({
				show: true,
				contents: {
					type: "error",
					heading: "Invalid URL",
					message: "Please enter a valid URL for the grant (e.g., https://example.com)"
				}
			});
			return;
		}

		// Validate Deadline is a future date (skip for Ongoing/Closed/Temporarily Closed or in merge/QA mode)
		if (!mergeMode) {
			const deadlineStr = (formData.Deadline || '').trim().toLowerCase();
			const skipDateCheck = ['ongoing', 'closed', 'temporarily closed'].includes(deadlineStr);
			if (!skipDateCheck) {
				const deadlineDate = new Date(formData.Deadline);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				if (isNaN(deadlineDate.getTime()) || deadlineDate < today) {
					setErrorGrantNotif({
						show: true,
						contents: {
							type: "error",
							heading: "Invalid Deadline",
							message: "Enter a future date (e.g. 2024-12-31) or use Ongoing, Closed, or Temporarily Closed."
						}
					});
					return;
				}
			}
		}

		try {
			let updatedRecord;
			
			// If we have a scraped record ID, update it instead of creating
			if (scrapedRecordId) {
				updatedRecord = await base("deduped_grants").update([
					{
						id: scrapedRecordId,
						fields: {
							...formData,
							Amount: Number(formData.Amount)
						}
					}
				]);
				
				// Update grants list
				setGrants(prev => prev.map(g => g.id === scrapedRecordId ? updatedRecord[0] : g));
				
				setContents(
					{
						type: "success",
						heading: "Grant Updated",
						message: `"${formData.Name}" has been updated successfully.`
					},
					setSubmitGrantNotif
				);
			} else {
				// Create new record if no scraped record exists
				const newRecord = await base("deduped_grants").create([
					{
						fields: {
							...formData,
							Amount: Number(formData.Amount)
						}
					}
				]);
				
				updatedRecord = newRecord;
				setGrants(prev => [...prev, newRecord[0]]);
				
				setContents(
					{
						type: "success",
						heading: "Grant Added",
						message: `"${formData.Name}" has been added successfully.`
					},
					setSubmitGrantNotif
				);
			}
			
			// Show save/update notification first
			setShowNotif(true, setSubmitGrantNotif);

			// Merge mode: tell parent to mark staged as promoted and advance to next (modal stays open)
			if (mergeMode && onMergeComplete && stagedGrant?.id) {
				onMergeComplete(stagedGrant.id);
			}
			
			// Add to company roadmap if company is selected (this will show its own notification)
			if (selectedCompany && updatedRecord[0]) {
				setTimeout(async () => {
					await addGrantToCompanyRoadmap(updatedRecord[0]);
				}, 500);
			}
			
			// Close modal after a short delay (only when not in merge mode; in merge mode we advance to next)
			if (!mergeMode) {
				setTimeout(() => {
					handleClose();
				}, selectedCompany ? 2000 : 1000);
			}
		} catch (error) {
			console.error('Error adding grant:', error);
			
			// Handle specific Airtable errors
			let errorMessage = "An error occurred while adding the grant.";
			
			if (error.error?.type === 'INVALID_FORMULA') {
				errorMessage = "Invalid data format in one or more fields.";
			} else if (error.error?.type === 'INVALID_VALUE') {
				errorMessage = "One or more fields contain invalid values.";
			} else if (error.error?.type === 'INVALID_PERMISSIONS') {
				errorMessage = "You don't have permission to add grants. Please contact an administrator.";
			} else if (error.error?.type === 'INVALID_REQUEST') {
				errorMessage = "The request was invalid. Please check all field values.";
			} else if (error.error?.type === 'RATE_LIMIT_EXCEEDED') {
				errorMessage = "Too many requests. Please try again in a few minutes.";
			} else if (error.error?.type === 'SERVER_ERROR') {
				errorMessage = "A server error occurred. Please try again later.";
			}

			setErrorGrantNotif({
				show: true,
				contents: {
					type: "error",
					heading: "Error Adding Grant",
					message: errorMessage
				}
			});
		}
	};
	
	// Load companies for selector
	const loadCompanies = useCallback(async (inputValue = '') => {
		try {
			let filterFormula = '';
			
			if (inputValue && inputValue.trim()) {
				const searchTerm = inputValue.trim().replace(/'/g, "''");
				filterFormula = `SEARCH(LOWER("${searchTerm}"), LOWER({Name}))`;
			}

			const records = await base("companies")
				.select({
					view: "Grid view",
					filterByFormula: filterFormula || undefined,
					maxRecords: 100
				})
				.all();

			return records.map(record => ({
				value: record,
				label: record.fields?.Name || 'Unnamed Company'
			}));
		} catch (error) {
			console.error('Error loading companies:', error);
			return [];
		}
	}, []);

	// Load user's company on mount
	useEffect(() => {
		if (user?.primaryEmailAddress?.emailAddress && !selectedCompany) {
			setIsLoadingUserCompany(true);
			const loadUserCompany = async () => {
				try {
					const userDomain = extractDomain(user.primaryEmailAddress.emailAddress);
					let records = await base("companies")
						.select({
							filterByFormula: `{Domain} = '${userDomain}'`,
							maxRecords: 1
						})
						.all();
					
					if (records.length === 0) {
						const domainBase = userDomain.split('.')[0];
						records = await base("companies")
							.select({
								filterByFormula: `FIND("${domainBase}", LOWER({Name})) > 0`,
								maxRecords: 1
							})
							.all();
					}
					
					if (records.length > 0) {
						setSelectedCompany(records[0]);
						setDefaultCompanyOption({
							value: records[0],
							label: records[0].fields?.Name || 'Unnamed Company'
						});
					}
				} catch (error) {
					console.error('Error loading user company:', error);
				} finally {
					setIsLoadingUserCompany(false);
				}
			};
			loadUserCompany();
		}
	}, [user, selectedCompany]);

	// Merge mode: populate from staged grant and fetch potential duplicates from production
	useEffect(() => {
		if (!mergeMode || !stagedGrant?.fields || !isOpen) return;
		populateFormFromPreview(stagedGrant, stagedGrant.fields.GrantUrl || '');
		setShowPreview(true);
		setHasScraped(true);
		setScrapedRecordId(null); // will set if user picks an existing production grant
		setPreviewData(stagedGrant);
		setIsDuplicate(false);
		// Fetch potential duplicates from deduped_grants (by name and URL), exclude archived
		const name = (stagedGrant.fields.Name || '').trim();
		const url = (stagedGrant.fields.GrantUrl || '').trim();
		const loadDuplicates = async () => {
			try {
				const all = await base("deduped_grants")
					.select({ fields: ["Name", "GrantUrl", "Amount", "Deadline", "Description", "Eligibility_Criteria", "Eligible_Industry", "Eligible_Region", "Eligible_Employees", "Eligible_Founded", "Eligible_Revenue", "ExcitingBenefitContent", "Archived"] })
					.all();
				const norm = (s) => (s || '').toLowerCase().trim();
				const nameNorm = norm(name);
				const urlNorm = url ? normalizeUrl(url) : '';
				const notArchived = (g) => String(g.fields?.Archived) !== "true";
				const duplicates = all.filter((g) => {
					if (!notArchived(g)) return false;
					const gName = norm(g.fields?.Name);
					const gUrl = g.fields?.GrantUrl ? normalizeUrl(g.fields.GrantUrl) : '';
					return (nameNorm && gName === nameNorm) || (urlNorm && gUrl === urlNorm);
				});
				setPotentialDuplicates(duplicates);
			} catch (e) {
				console.error('Error loading potential duplicates:', e);
				setPotentialDuplicates([]);
			}
		};
		loadDuplicates();
	}, [mergeMode, stagedGrant, isOpen]);

	// Add grant to company's earnings roadmap
	const addGrantToCompanyRoadmap = async (grant) => {
		if (!selectedCompany || !grant) return;

		try {
			const grantName = grant.fields?.Name || formData.Name;
			if (!grantName) {
				throw new Error('Grant name is missing');
			}

			const currentInterestedIn = selectedCompany.fields?.InterestedIn || '';
			const interestedInList = currentInterestedIn 
				? currentInterestedIn.split(',').map(item => item.trim()).filter(item => item)
				: [];
			
			if (interestedInList.includes(grantName)) {
				setContents(
					{
						type: "info",
						heading: "Already Added",
						message: `"${grantName}" is already in ${selectedCompany.fields?.Name}'s Earnings Roadmap.`
					},
					setSubmitGrantNotif
				);
				setShowNotif(true, setSubmitGrantNotif);
				return;
			}

			interestedInList.push(grantName);
			const updatedInterestedIn = interestedInList.join(',');
			const updatedMissingInfoFor = addGrantIdToMissingInfoFor(
				selectedCompany.fields?.MissingInfoFor,
				grant.id
			);

			await base("companies").update([{
				id: selectedCompany.id,
				fields: {
					InterestedIn: updatedInterestedIn,
					MissingInfoFor: updatedMissingInfoFor,
				}
			}]);

			setContents(
				{
					type: "success",
					heading: "Grant Added to Roadmap",
					message: `"${grantName}" added to ${selectedCompany.fields?.Name}'s earnings roadmap`
				},
				setSubmitGrantNotif
			);
			setShowNotif(true, setSubmitGrantNotif);
		} catch (error) {
			console.error('Error adding grant to roadmap:', error);
			setErrorGrantNotif({
				show: true,
				contents: {
					type: "error",
					heading: "Failed to Add to Roadmap",
					message: `Failed to add grant to roadmap: ${error.message || 'Unknown error'}`
				}
			});
		}
	};

	// Handle modal close
	const handleClose = () => {
		setShowPreview(false);
		setPreviewData(null);
		setPotentialDuplicates([]);
		setUrl('');
		setHasScraped(false);
		setIsScraping(false);
		setShowDebug(false);
		setShowDebugJson(false);
		setLeftStagedTab('details');
		setIsDuplicate(false);
		setScrapedRecordId(null);
		setSelectedCompany(null);
		setDefaultCompanyOption(null);
		setUrlCheckState({ status: 'idle', message: null, statusCode: null });
		setFormData({
			Name: '',
			Amount: '',
			Deadline: '',
			GrantUrl: '',
			Description: '',
			Eligibility_Criteria: '',
			Eligible_Industry: '',
			Eligible_Region: '',
			Eligible_Employees: '',
			Eligible_Founded: '',
			Eligible_Revenue: '',
			ExcitingBenefitContent: ''
		});
		onClose();
	};
	
	// Auto-hide notifications
	useEffect(() => {
		if (errorGrantNotif.show) {
			const timer = setTimeout(() => {
				setErrorGrantNotif({ show: false, contents: null });
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [errorGrantNotif.show]);
	
	return (
		<>
			<Transition.Root show={isOpen} as={Fragment}>
				<Dialog as="div" className="relative z-10" onClose={handleClose}>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
					</Transition.Child>

					<div className="fixed inset-0 z-10 overflow-y-auto">
						<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:pt-20 sm:pb-8 sm:px-4">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
								enterTo="opacity-100 translate-y-0 sm:scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 translate-y-0 sm:scale-100"
								leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							>
								<Dialog.Panel className={`relative transform overflow-hidden rounded-2xl bg-white px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:w-full overflow-y-auto ${mergeMode ? 'sm:max-w-7xl max-h-[90vh]' : 'sm:max-w-3xl max-h-[calc(100vh-8rem)]'}`}>
									<div>
										<div className="mb-4">
											<Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 mb-1">
												{mergeMode ? 'QA & Merge: Staged → Production' : 'Add New Grant'}
											</Dialog.Title>
											<p className="text-sm text-gray-500">
												{mergeMode ? 'Compare staged grant with production. Pick a duplicate to update, or save as new.' : 'Enter a grant URL to scrape and add to our dataset'}
											</p>
										</div>

										{/* Company Selector — first (for roadmap and merge context) */}
										<div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Add to Company Earnings Roadmap (Optional)
												{selectedCompany && user?.primaryEmailAddress?.emailAddress && 
													selectedCompany.fields.Domain === extractDomain(user.primaryEmailAddress.emailAddress) && (
													<span className="text-xs text-green-600 ml-2">(Auto-selected from your email domain)</span>
												)}
											</label>
											<AsyncSelect
												loadOptions={loadCompanies}
												value={selectedCompany ? { value: selectedCompany, label: selectedCompany.fields?.Name || 'Unnamed Company' } : null}
												onChange={(option) => {
													setSelectedCompany(option?.value || null);
													if (!option) setDefaultCompanyOption(null);
												}}
												placeholder={isLoadingUserCompany ? "Loading your company..." : "Select company (optional)..."}
												className="w-full"
												classNamePrefix="select"
												isSearchable={true}
												isClearable={true}
												defaultOptions={defaultCompanyOption ? [defaultCompanyOption] : false}
												noOptionsMessage={({ inputValue }) => inputValue ? `No companies found matching "${inputValue}"` : "Type to search companies..."}
												loadingMessage={() => "Searching companies..."}
												menuPortalTarget={document.body}
												menuPosition="fixed"
												cacheOptions={true}
												debounceMs={300}
												styles={{
													control: (base) => ({ ...base, backgroundColor: "white", borderColor: "rgb(209, 213, 219)", color: "rgb(17, 24, 39)", minHeight: "40px", fontSize: "14px" }),
													singleValue: (base) => ({ ...base, color: "rgb(17, 24, 39)" }),
													placeholder: (base) => ({ ...base, color: "rgb(107, 114, 128)" }),
													menu: (base) => ({ ...base, backgroundColor: "white", border: "1px solid rgb(209, 213, 219)", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }),
													option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? "rgb(243, 244, 246)" : "transparent", color: "rgb(17, 24, 39)" }),
												}}
											/>
											{selectedCompany && (
												<p className="mt-2 text-xs text-gray-600">
													This grant will be added to <span className="font-medium">{selectedCompany.fields?.Name}</span>'s Earnings Roadmap when you save.
												</p>
											)}
										</div>

										{/* Debug Information — below company select */}
										{(showPreview && previewData) && (
											<div className="mb-4 border-t border-gray-200 pt-4">
												<button
													type="button"
													onClick={() => setShowDebug(!showDebug)}
													className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
												>
													<span>Debug Information</span>
													<svg className={`w-5 h-5 text-gray-500 transition-transform ${showDebug ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
													</svg>
												</button>
												{showDebug && (
													<div className="mt-3 space-y-3">
														{previewData.fields && !mergeMode && (
															<div className="bg-gray-50 rounded-lg p-4">
																<h4 className="text-xs font-medium text-gray-500 mb-2 uppercase">Scraped Grant Fields</h4>
																<div className="space-y-1 max-h-[40vh] overflow-y-auto">
																	{Object.entries(previewData.fields).map(([key, value]) => (
																		<div key={key} className="text-xs text-gray-800">
																			<span className="font-semibold">{key}:</span>{' '}
																			{typeof value === 'object' && value !== null ? (
																				<span>{Object.entries(value).map(([subKey, subVal]) => <span key={subKey} className="ml-2"><span className="font-medium">{subKey}:</span> {String(subVal)}</span>)}</span>
																			) : (
																				<span>{String(value)}</span>
																			)}
																		</div>
																	))}
																</div>
															</div>
														)}
														<div className="bg-gray-50 rounded-lg p-4">
															<button
																type="button"
																onClick={() => setShowDebugJson(!showDebugJson)}
																className="flex items-center justify-between w-full text-left text-xs font-medium text-gray-500 hover:text-gray-700 uppercase"
															>
																<span>Debug JSON</span>
																<svg className={`w-4 h-4 text-gray-500 transition-transform ${showDebugJson ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
																</svg>
															</button>
															{showDebugJson && (
																<div className="mt-2 bg-white p-3 rounded-md max-h-[30vh] overflow-y-auto border border-gray-200">
																	<pre className="text-xs text-gray-700 whitespace-pre-wrap">{JSON.stringify(previewData, null, 2)}</pre>
																</div>
															)}
														</div>
													</div>
												)}
											</div>
										)}

										{/* URL Input + Scrape — only when not merge mode */}
										{!mergeMode && (
										<div className="mb-6">
											<label className="block text-sm font-medium text-gray-700 mb-2">Grant URL</label>
											<div className="flex rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-black focus-within:border-black">
												<input
													type="url"
													className="flex-1 px-4 py-2.5 text-sm border-0 focus:ring-0 focus:outline-none"
													placeholder="Enter grant URL to scrape"
													value={url}
													onChange={(event) => setUrl(event.target.value)}
													onKeyDown={(event) => handleKeyDown(event)}
													disabled={isScraping}
												/>
												<button
													type="button"
													onClick={handleScrape}
													disabled={!url.trim() || isScraping}
													className="px-6 py-2.5 bg-black text-white text-sm font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
												>
													{isScraping ? 'Scraping...' : 'Scrape'}
												</button>
											</div>
										</div>
										)}

										{/* Duplicate Warning */}
										{isDuplicate && (
											<div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
												<div className="flex">
													<div className="flex-shrink-0">
														<svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
															<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
														</svg>
													</div>
													<div className="ml-3">
														<h3 className="text-sm font-medium text-amber-800">Grant Already Exists</h3>
														<div className="mt-2 text-sm text-amber-700">
															<p>This grant URL is already in the database. The existing grant details are shown below. You can edit and save as a new grant.</p>
														</div>
													</div>
												</div>
											</div>
										)}

										{/* Potential Duplicates — from production table (deduped_grants); click to load, Archive to hide from list and finder */}
										{potentialDuplicates.length > 0 && !isDuplicate && (
											<div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
												<h4 className="text-sm font-medium text-yellow-800 mb-1">Potential Duplicates Found</h4>
												<p className="text-xs text-yellow-700 mb-2">From production table (deduped_grants). {mergeMode ? 'Click a row to load it into the Production (right) form and update that record.' : 'Similar grants from the same domain.'} Use Archive to hide a grant from this list and from the main grants finder.</p>
												<div className="space-y-2">
													{potentialDuplicates.map((duplicate, index) => (
														<div
															key={duplicate.id || index}
															className={`flex items-center gap-2 bg-white p-3 rounded-md border transition-colors ${scrapedRecordId === duplicate.id ? 'border-black ring-2 ring-black' : 'border-yellow-200 hover:border-yellow-400 hover:bg-yellow-50'}`}
														>
															<button
																type="button"
																onClick={() => {
																	populateFormFromPreview(duplicate, duplicate.fields?.GrantUrl || '');
																	setScrapedRecordId(duplicate.id);
																	setIsDuplicate(true);
																}}
																className="flex-1 text-left min-w-0"
															>
																<p className="text-sm font-medium text-gray-900">{duplicate.fields?.Name}</p>
																<p className="text-xs text-gray-600 mt-1">URL: {duplicate.fields?.GrantUrl}</p>
																<p className="text-xs text-gray-600">Amount: ${duplicate.fields?.Amount != null ? Number(duplicate.fields.Amount).toLocaleString() : '—'}</p>
																{scrapedRecordId === duplicate.id && <span className="text-xs text-green-600 font-medium">✓ Loaded into Production form</span>}
															</button>
															<button
																type="button"
																onClick={async (e) => {
																	e.stopPropagation();
																	try {
																		await base("deduped_grants").update([{ id: duplicate.id, fields: { Archived: "true" } }]);
																		setGrants(prev => (prev || []).map(g => g.id === duplicate.id ? { ...g, fields: { ...g.fields, Archived: "true" } } : g));
																		setPotentialDuplicates(prev => prev.filter(r => r.id !== duplicate.id));
																	} catch (err) {
																		console.error('Error archiving grant:', err);
																	}
																}}
																className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200"
															>
																Archive
															</button>
														</div>
													))}
												</div>
											</div>
										)}

										{/* Form Fields - Only show after scraping (or in merge mode we already have data) */}
										{hasScraped && (
											<div className={`space-y-5 border-t border-gray-200 pt-6 ${mergeMode ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : ''}`}>
												{/* Merge mode: left column = tabs (Details | Website iframe) */}
												{mergeMode && stagedGrant?.fields && (
													<div className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col min-h-0">
														<div className="flex border-b border-gray-200 px-2 pt-2">
															<button
																type="button"
																onClick={() => setLeftStagedTab('details')}
																className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${leftStagedTab === 'details' ? 'bg-white border border-b-0 border-gray-200 text-gray-900 -mb-px' : 'text-gray-600 hover:text-gray-900'}`}
															>
																Details
															</button>
															<button
																type="button"
																onClick={() => setLeftStagedTab('website')}
																disabled={!stagedGrant.fields.GrantUrl && !formData.GrantUrl}
																className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${leftStagedTab === 'website' ? 'bg-white border border-b-0 border-gray-200 text-gray-900 -mb-px' : 'text-gray-600 hover:text-gray-900'} disabled:opacity-50 disabled:cursor-not-allowed`}
															>
																Website
															</button>
															<button
																type="button"
																onClick={() => setLeftStagedTab('inputMatcher')}
																className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${leftStagedTab === 'inputMatcher' ? 'bg-white border border-b-0 border-gray-200 text-gray-900 -mb-px' : 'text-gray-600 hover:text-gray-900'}`}
															>
																Input Matcher
															</button>
														</div>
														<div className="p-4 flex-1 min-h-0 overflow-hidden flex flex-col">
															{leftStagedTab === 'details' && (
																<>
																	<h4 className="text-sm font-semibold text-gray-800 mb-3">Staged (deduped_grants_newcopy)</h4>
																	<div className="space-y-2 text-sm mb-4">
																		<p><span className="font-medium text-gray-600">Name:</span> {stagedGrant.fields.Name || '—'}</p>
																		<p><span className="font-medium text-gray-600">Amount:</span> {stagedGrant.fields.Amount != null ? `$${Number(stagedGrant.fields.Amount).toLocaleString()}` : '—'}</p>
																		<p><span className="font-medium text-gray-600">Deadline:</span> {stagedGrant.fields.Deadline || '—'}</p>
																		<p><span className="font-medium text-gray-600">URL:</span>{' '}
																			{stagedGrant.fields.GrantUrl ? (
																				<a href={stagedGrant.fields.GrantUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block">{stagedGrant.fields.GrantUrl}</a>
																			) : '—'}
																		</p>
																		{stagedGrant.fields.Description && <p><span className="font-medium text-gray-600">Description:</span> <span className="line-clamp-3">{stagedGrant.fields.Description}</span></p>}
																		{stagedGrant.fields.Eligible_Region && <p><span className="font-medium text-gray-600">Region:</span> {stagedGrant.fields.Eligible_Region}</p>}
																	</div>
																	<h4 className="text-xs font-medium text-gray-500 mb-2 uppercase">Scraped Grant Fields</h4>
																	<div className="space-y-1 max-h-[30vh] overflow-y-auto text-xs text-gray-800">
																		{Object.entries(stagedGrant.fields).map(([key, value]) => (
																			<div key={key}>
																				<span className="font-semibold">{key}:</span>{' '}
																				{typeof value === 'object' && value !== null ? (
																					<span>{Object.entries(value).map(([subKey, subVal]) => <span key={subKey} className="ml-2"><span className="font-medium">{subKey}:</span> {String(subVal)}</span>)}</span>
																				) : (
																					<span>{String(value)}</span>
																				)}
																			</div>
																		))}
																	</div>
																</>
															)}
															{leftStagedTab === 'website' && (() => {
																const formUrl = (formData.GrantUrl || '').trim();
																let iframeUrl = stagedGrant.fields.GrantUrl || '';
																if (formUrl) {
																	try {
																		new URL(formUrl);
																		iframeUrl = formUrl;
																	} catch (_) {}
																}
																return (
																	<div className="flex-1 min-h-[50vh] flex flex-col">
																		{iframeUrl ? (
																			<>
																				{formUrl && iframeUrl === formUrl && (
																					<p className="text-xs text-gray-500 mb-1">Showing URL from form (right): {formUrl}</p>
																				)}
																				<iframe
																					key={iframeUrl}
																					title="Grant website"
																					src={iframeUrl}
																					className="w-full flex-1 min-h-[50vh] border border-gray-200 rounded-lg bg-white"
																					sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
																					referrerPolicy="no-referrer"
																				/>
																			</>
																		) : (
																			<p className="text-sm text-gray-500">No URL for this grant. Enter a Grant URL in the form (right) to preview.</p>
																		)}
																	</div>
																);
															})()}
															{leftStagedTab === 'inputMatcher' && (() => {
																const f = stagedGrant.fields;
																const stagedVal = (key) => {
																	const v = f[key];
																	if (v == null || v === '') return '';
																	return Array.isArray(v) ? v.join(', ') : String(v);
																};
																return (
																	<div className="overflow-y-auto">
																		<h4 className="text-sm font-semibold text-gray-800 mb-3">Staged (same layout as right — read-only)</h4>
																		<div className="grid grid-cols-1 gap-5">
																			<div>
																				<label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
																				<input type="text" readOnly value={stagedVal('Name')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																			</div>
																			<div className="grid grid-cols-2 gap-4">
																				<div>
																					<label className="block text-sm font-medium text-gray-700 mb-1.5">Amount</label>
																					<input type="text" readOnly value={stagedVal('Amount')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																				</div>
																				<div>
																					<label className="block text-sm font-medium text-gray-700 mb-1.5">Deadline</label>
																					<div className="flex flex-wrap gap-2 items-center">
																						<input type="text" readOnly value={stagedVal('Deadline')} className="block flex-1 min-w-[12rem] rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																					</div>
																				</div>
																			</div>
																			<div>
																				<label className="block text-sm font-medium text-gray-700 mb-1.5">Benefit</label>
																				<textarea readOnly value={stagedVal('ExcitingBenefitContent')} rows={2} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																			</div>
																			<div>
																				<label className="block text-sm font-medium text-gray-700 mb-1.5">Grant URL</label>
																				<input type="text" readOnly value={stagedVal('GrantUrl')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																			</div>
																			<div>
																				<label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
																				<textarea readOnly value={stagedVal('Description')} rows={3} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																			</div>
																			<div>
																				<label className="block text-sm font-medium text-gray-700 mb-1.5">Eligibility Criteria</label>
																				<textarea readOnly value={stagedVal('Eligibility_Criteria')} rows={3} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																			</div>
																			<div className="grid grid-cols-2 gap-4">
																				<div>
																					<label className="block text-sm font-medium text-gray-700 mb-1.5">Eligible Industry</label>
																					<input type="text" readOnly value={stagedVal('Eligible_Industry')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																				</div>
																				<div>
																					<label className="block text-sm font-medium text-gray-700 mb-1.5">Eligible Region</label>
																					<input type="text" readOnly value={stagedVal('Eligible_Region')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																				</div>
																			</div>
																			<div className="grid grid-cols-3 gap-4">
																				<div className="min-w-0">
																					<label className="block text-sm font-medium text-gray-700 mb-1.5">Min Employees</label>
																					<input type="text" readOnly value={stagedVal('Eligible_Employees')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																				</div>
																				<div className="min-w-0">
																					<label className="block text-sm font-medium text-gray-700 mb-1.5">Required Years In Operation</label>
																					<input type="text" readOnly value={stagedVal('Eligible_Founded')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																				</div>
																				<div className="min-w-0">
																					<label className="block text-sm font-medium text-gray-700 mb-1.5">Min Revenue</label>
																					<input type="text" readOnly value={stagedVal('Eligible_Revenue')} className="block w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm px-3 py-2" />
																				</div>
																			</div>
																		</div>
																	</div>
																);
															})()}
														</div>
													</div>
												)}
												
												<div className={mergeMode ? '' : 'grid grid-cols-1'}>
													{mergeMode && <h4 className="text-sm font-semibold text-gray-800 mb-3">Production (deduped_grants) — edit and save</h4>}
												<div className="grid grid-cols-1 gap-5">
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
														<input
															type="text"
															name="Name"
															value={formData.Name}
															onChange={handleInputChange}
															className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
														/>
													</div>
													<div className="grid grid-cols-[1fr_3fr] gap-4">
														<div className="min-w-0">
															<label className="block text-sm font-medium text-gray-700 mb-1.5">Amount</label>
															<input
																type="number"
																name="Amount"
																value={formData.Amount}
																onChange={handleInputChange}
																className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
															/>
														</div>
														<div className="min-w-0">
															<label className="block text-sm font-medium text-gray-700 mb-1.5">Deadline</label>
															<div className="flex gap-2 items-center flex-wrap">
																<input
																	type="date"
																	value={isDeadlineDate(formData.Deadline) ? formData.Deadline : ''}
																	onChange={(e) => setFormData(prev => ({ ...prev, Deadline: e.target.value || '' }))}
																	className="rounded-lg border border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
																/>
																<CreatableSelect
																	options={deadlinePresetOptions}
																	value={!isDeadlineDate(formData.Deadline) && formData.Deadline ? { value: formData.Deadline, label: formData.Deadline } : null}
																	onChange={(option) => setFormData(prev => ({ ...prev, Deadline: option ? option.value : '' }))}
																	placeholder="Or Ongoing, Closed, custom..."
																	isClearable
																	className="flex-1 min-w-[10rem] react-select-container"
																	classNamePrefix="react-select"
																	styles={{ control: (base) => ({ ...base, minHeight: 38, borderRadius: '0.5rem' }) }}
																/>
															</div>
														</div>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1.5">Benefit</label>
														<textarea
															name="ExcitingBenefitContent"
															value={formData.ExcitingBenefitContent}
															onChange={handleInputChange}
															rows={2}
															placeholder="e.g. 50% of cross border sales, marketing, travel, and consulting"
															className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
														/>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1.5">Grant URL</label>
														<div className="flex gap-2 items-center flex-wrap">
															<input
																type="url"
																name="GrantUrl"
																value={formData.GrantUrl}
																onChange={handleInputChange}
																className="block flex-1 min-w-0 rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
															/>
															<button
																type="button"
																onClick={handleCheckUrl}
																disabled={urlCheckState.status === 'checking'}
																className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
															>
																{urlCheckState.status === 'checking' ? 'Checking…' : 'Check URL'}
															</button>
														</div>
														{urlCheckState.status !== 'idle' && urlCheckState.status !== 'checking' && urlCheckState.message && (
															<p className={`mt-1.5 text-sm ${urlCheckState.status === 'ok' ? 'text-green-700' : urlCheckState.status === 'broken' ? 'text-amber-700' : 'text-red-600'}`}>
																{urlCheckState.status === 'ok' && '✓ '}
																{urlCheckState.status === 'broken' && '⚠ '}
																{urlCheckState.message}
															</p>
														)}
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
														<textarea
															name="Description"
															value={formData.Description}
															onChange={handleInputChange}
															rows={3}
															className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
														/>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1.5">Eligibility Criteria</label>
														<textarea
															name="Eligibility_Criteria"
															value={formData.Eligibility_Criteria}
															onChange={handleInputChange}
															rows={3}
															className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
														/>
													</div>
													<div className="grid grid-cols-2 gap-4">
														<div>
															<label className="block text-sm font-medium text-gray-700 mb-1.5">Eligible Industry</label>
															<Select
																isMulti
																options={eligibleIndustryOptions}
																value={parseMultiSelect(formData.Eligible_Industry, eligibleIndustryOptions)}
																onChange={(selected) => setFormData((prev) => ({ ...prev, Eligible_Industry: multiToStored(selected) }))}
																placeholder="Select industries..."
																className="react-select-container"
																classNamePrefix="react-select"
																styles={{ control: (base) => ({ ...base, minHeight: 38, borderRadius: '0.5rem' }) }}
															/>
														</div>
														<div>
															<label className="block text-sm font-medium text-gray-700 mb-1.5">Eligible Region</label>
															<Select
																isMulti
																options={regionOptions}
																value={parseMultiSelect(formData.Eligible_Region, regionOptions)}
																onChange={(selected) => setFormData((prev) => ({ ...prev, Eligible_Region: multiToStored(selected) }))}
																placeholder="Select regions..."
																className="react-select-container"
																classNamePrefix="react-select"
																styles={{ control: (base) => ({ ...base, minHeight: 38, borderRadius: '0.5rem' }) }}
															/>
														</div>
													</div>
													<div className="grid grid-cols-3 gap-4">
														<div className="min-w-0">
															<label className="block text-sm font-medium text-gray-700 mb-1.5">Min Employees</label>
															<input
																type="text"
																name="Eligible_Employees"
																value={formData.Eligible_Employees}
																onChange={handleInputChange}
																className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
															/>
														</div>
														<div className="min-w-0">
															<label className="block text-sm font-medium text-gray-700 mb-1.5">Required Years In Operation</label>
															<input
																type="text"
																name="Eligible_Founded"
																value={formData.Eligible_Founded}
																onChange={handleInputChange}
																className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
															/>
														</div>
														<div className="min-w-0">
															<label className="block text-sm font-medium text-gray-700 mb-1.5">Min Revenue</label>
															<input
																type="text"
																name="Eligible_Revenue"
																value={formData.Eligible_Revenue}
																onChange={handleInputChange}
																placeholder="e.g. $500,000 or revenue range"
																className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm px-3 py-2"
															/>
														</div>
													</div>
												</div>
												</div>
											</div>
										)}

										{/* Action Buttons */}
										{hasScraped && (
											<div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-6 flex-wrap">
												{mergeMode && onMergeComplete && stagedGrant?.id && (
													<button
														type="button"
														onClick={() => onMergeComplete(stagedGrant.id)}
														className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
													>
														Skip (mark as merged)
													</button>
												)}
												<button
													type="button"
													onClick={handleClose}
													className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
												>
													Cancel
												</button>
												<button
													type="button"
													onClick={handleSubmitGrant}
													className="px-6 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
												>
													{mergeMode ? (scrapedRecordId ? 'Update production grant' : 'Create new in production') : (scrapedRecordId ? 'Update Grant' : 'Save Grant')}
												</button>
											</div>
										)}
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition.Root>
			
			{submitGrantNotif && (
				<Notification
					showNotif={submitGrantNotif}
					setShowNotif={setSubmitGrantNotif}
					contents={{
						type: "success",
						heading: "Success!",
						message: "Grant has been added successfully."
					}}
				/>
			)}
			{errorGrantNotif.show && (
				<Notification
					showNotif={errorGrantNotif.show}
					setShowNotif={(val) => setErrorGrantNotif({ ...errorGrantNotif, show: val })}
					contents={errorGrantNotif.contents || {
						type: "error",
						heading: "Error!",
						message: "Failed to add grant. Please try again."
					}}
				/>
			)}
		</>
	);
};

export default AddGrantModal;

